import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { compilationDataProps } from '../../pages/TermCompilerPage';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import React from 'react';
import { getTextWithToolTip } from './riskPatientDetailsHelper';
import { ContractChipList } from '../../_components/atoms/ComparisonTable';
import { getAvatarIcon } from './TaskBoardHelper';
import { formatDate } from './helper';
import {
  cellStyles3,
  Columns,
  Tooltip,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import clsx from 'clsx';
import axios from 'axios';
import properties from '../properties';
import { DottedText } from './financialForensicsHelper';

export interface compilationObjectProps {
  term_compiler_id: string;
  group: string;
  group_name: string;
  compiler_name: string;
  description: string;
  use_case: string;
  use_case_name: string;
  is_default_use_case: boolean;
  contract_ids: string[];
  plan_docs: string[];
  selected_terms: any[];
  status: string;
}

export const formatCompilationTableData = (data: compilationDataProps[]) => {
  const tableData: any[] = [];

  data.map((item: compilationDataProps) => {
    const source: any[] = [];
    item?.contracts?.map((cont: any) =>
      source.push(cont.health_care_partner_id?.health_care_partner),
    );
    item?.plan_docs?.map((plan: any) =>
      source.push(plan.benefit_types.join(' | ')),
    );
    const otherData = [];
    otherData.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.text.highemphasis}
      >
        {item?.use_case?.use_case}
      </Typography>,
    );
    otherData.push(<ContractChipList selectedItems={source} />);
    otherData.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.text.highemphasis}
      >
        {`${item?.termCount ?? ''}`}
      </Typography>,
    );
    otherData.push(getAvatarIcon(item?.updatedBy));
    otherData.push(
      <Typography
        fontSize="14px"
        fontType="medium"
        textColor={patientAccessTheme.palette.text.highemphasis}
        styleProps={{
          width: '90px',
        }}
      >
        {formatDate(item?.updatedAt, 'YYYY-MM-DD')}
      </Typography>,
    );

    tableData.push({
      company_name: (
        <Typography
          fontSize="14px"
          fontType="medium"
          textColor={patientAccessTheme.palette.text.primary[500]}
          styleProps={{ width: '170px' }}
        >
          {getTextWithToolTip(item?.compiler_name || '', 20)}
        </Typography>
      ),
      compiler_name: item.compiler_name,
      status: item?.status,
      use_case: item?.use_case?.use_case,
      contract_ids: item?.contracts,
      plan_docs: item?.plan_docs,
      _id: item?._id,
      eg_nid: item.eg_nid,
      selected_terms: item.selected_terms,
      otherData,
    });
  });
  return tableData;
};

export type LocationState = {
  term_compiler_id: string;
};

export type EvidenceDataType = {
  excerpt: string;
  document_section: string;
};

export type ChunkDataType = {
  chunk_id: string;
  chunk_type: string;
  grounding: any[];
  text: string;
};

export type ExtractionsDataType = {
  value: any;
  confidence_score: 'high' | 'medium' | 'low';
  score_rationale: string;
  source: string;
  evidence: EvidenceDataType[];
  chunks: ChunkDataType[];
};

export type TermsDataType = {
  term: string;
  description: string;
  value: any;
  confidence_score: 'high' | 'medium' | 'low';
  score_rationale: string;
  extractions: ExtractionsDataType[];
};

export type SelectedTermsDataType = {
  topic: string;
  terms: TermsDataType[];
};

export type SelectedTopicDataType = {
  topic: string;
  label: string;
  term_topics: SelectedTermsDataType[];
};

export type TermCompilerDataType = {
  term_compiler_id: string;
  compiler_name: string;
  eg_nid: string;
  date_created: string;
  status: string;
  selected_terms: SelectedTopicDataType[];
};

export type DocumentDetailsDataType = {
  documentUrl: string;
  chunks: ChunkDataType[];
  pageNumber: number;
};
export interface TermCompilerContextType {
  loading: boolean;
  tableLoading: boolean;
  termCompilerData: TermCompilerDataType;
  filters: Record<string, string[]>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  openDocument: boolean;
  setOpenDocument: React.Dispatch<React.SetStateAction<boolean>>;
  documentDetails: DocumentDetailsDataType;
  setDocumentDetails: React.Dispatch<
    React.SetStateAction<DocumentDetailsDataType>
  >;
  documents: any[];
  setDocuments: React.Dispatch<React.SetStateAction<any[]>>;
}

const renderEvidence = (evidence: EvidenceDataType[], source: string) => {
  return evidence?.map((ev: any, j: number) => (
    <div key={j} style={{ marginBottom: '4px' }}>
      <div>
        <strong>Document Section/ Source:</strong> {ev?.document_section} /{' '}
        {source}
      </div>
      <div>
        <strong>Excerpt:</strong> {ev?.excerpt}
      </div>
      <br></br>
    </div>
  ));
};

export const formatColumns = (label: string): Columns[] => {
  const parts = label.split('+').map((p) => p.trim());

  const baseCols: Columns[] = [
    {
      name: 'Terms',
      width: '',
      align: 'left',
      cellStyles: cellStyles3,
    },
  ];

  const valueCols: Columns[] = parts.map((name) => {
    return {
      name: parts.length === 1 ? 'Value' : name,
      width: '',
      align: 'left',
      cellStyles: cellStyles3,
    };
  });

  const remarksCols: Columns[] =
    valueCols?.length > 1
      ? [
          {
            name: 'Remarks',
            width: '',
            align: 'left',
            cellStyles: cellStyles3,
          },
        ]
      : [];

  return [...baseCols, ...valueCols, ...remarksCols];
};

export const formatCategoryTableData = (
  data: SelectedTermsDataType[],
  classes: Record<string, string>,
  selectedTerms: string[] = [],
  onClickReference?: any,
) => {
  const tableData: any[] = [];

  if (data.length > 0) {
    data.forEach((item) => {
      const section: any[] = [
        <Typography fontSize="14px" fontType="medium" lineHeight="24px">
          {item.topic}
        </Typography>,
      ];

      item.terms.forEach((row: TermsDataType) => {
        // filter check
        if (selectedTerms.length > 0 && !selectedTerms.includes(row.term)) {
          return; // skip if not selected
        }
        if (row?.extractions?.length > 0) {
          const termRow = [
            <Box key={`${row.term}-term`} className={classes.parameterBox}>
              <Typography
                fontSize="14px"
                lineHeight="24px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
              >
                {row.term}
              </Typography>
              <Tooltip
                arrow
                classes={{
                  tooltip: classes.tooltip,
                  arrow: classes.arrow,
                  popper: classes.popper,
                }}
                title={row.description}
                placement="bottom-start"
              >
                <InfoOutlinedIcon
                  style={{
                    width: '18px',
                    height: '18px',
                    fill: patientAccessTheme.palette.status.icon,
                  }}
                />
              </Tooltip>
            </Box>,
             
            ...row.extractions.map((ext: ExtractionsDataType) => {
              return (
                <Box key={`${row.term}-value`} className={classes.parameterBox}>
                  <Typography fontSize="14px" lineHeight="24px" component="div">
                    <span>
                      {typeof ext.value === 'boolean'
                        ? ext.value
                          ? 'Yes'
                          : 'No'
                        : ext.value}
                    </span>
                    {ext?.chunks?.length > 0 ? (
                      Array.from(
                        new Map(
                           
                          ext.chunks.map((ref: any) => {
                            const key =
                              ref?.grounding?.extractions?.reference ??
                              ref?.grounding?.page;

                            return [key, ref]; // keep last occurrence
                          }),
                        ).values(),
                         
                      ).map((ref: any, ind: number, arr: any[]) => {
                        const reference =
                          ref?.grounding?.extractions?.reference;
                        const page = ref?.grounding?.page;
                        if (
                          reference ||
                          (page !== null && page !== undefined)
                        ) {
                          return (
                            <DottedText
                              text={
                                reference
                                  ? `${reference}`
                                  : `PgNo:${page + 1}${
                                      ind < arr.length - 1 ? ',' : ''
                                    }`
                              }
                              styles={{ paddingLeft: '5px' }}
                               
                              onClick={() =>
                                onClickReference(ext.source, ext.chunks, page)
                              }
                            />
                          );
                        }
                        return null;
                      })
                    ) : (
                      <Tooltip
                        arrow
                        classes={{
                          tooltip: clsx(classes.tooltip, classes.wideTooltip),
                          arrow: classes.arrow,
                          popper: classes.popper,
                        }}
                        title={renderEvidence(ext.evidence, ext.source)}
                        placement="bottom-start"
                      >
                        <InfoOutlinedIcon
                          style={{
                            width: '18px',
                            height: '18px',
                            fill: patientAccessTheme.palette.status.icon,
                            verticalAlign: 'middle',
                            marginLeft: '6px',
                          }}
                        />
                      </Tooltip>
                    )}
                  </Typography>
                </Box>
              );
            }),
          ];
          if (row.extractions.length > 1) {
            termRow.push(
              <Box
                key={`${row.term}-aggregate`}
                className={classes.parameterBox}
              >
                <Typography fontSize="14px" lineHeight="24px">
                  {typeof row.value === 'boolean'
                    ? row.value.toString()
                    : row.value}
                </Typography>
              </Box>,
            );
          }
          termRow.push(
            <Box
              key={`${row.term}-color`}
              style={{ backgroundColor: '#fff' }}
            />,
          );

          section.push(termRow);
        }
      });

      // only push section if it has rows
      if (section.length > 1) {
        tableData.push(section);
      }
    });
  }

  return tableData;
};

export const formatTermCompilerData = (data: any): TermCompilerDataType => {
  const termCompilerBaseData = data?.[0]?.term_compiler_id;
  const topicsMap: Record<string, SelectedTopicDataType> = {};

  data.forEach((item: any) => {
    const parentTopic = item.topic;

    if (!topicsMap[parentTopic]) {
      topicsMap[parentTopic] = {
        topic: parentTopic,
        label: item.label,
        term_topics: [],
      };
    }

    item.answer?.forEach((ans: any) => {
      // check if this sub-topic already exists inside parent
      let termTopic = topicsMap[parentTopic].term_topics.find(
         
        (tt) => tt.topic === ans.topic,
      );

      if (!termTopic) {
        termTopic = {
          topic: ans.topic,
          terms: [],
        };
        topicsMap[parentTopic].term_topics.push(termTopic);
      }

      const term: TermsDataType = {
        term: ans.term,
        description: ans.description,
        value: ans.value,
        confidence_score: ans.confidence_score,
        score_rationale: ans.score_rationale,
        extractions: (ans.extractions || []).map(
           
          (ex: any): ExtractionsDataType => ({
            value: ex.value,
            confidence_score: ex.confidence_score,
            score_rationale: ex.score_rationale,
            source: ex.source,
            evidence: (ex.evidence || []).map(
               
              (ev: any): EvidenceDataType => ({
                excerpt: ev.excerpt,
                document_section: ev.document_section,
              }),
            ),
            chunks: (ex.chunks || []).map(
               
              (ch: any): ChunkDataType => ({
                chunk_id: ch.chunk_id,
                chunk_type: ch.chunk_type,
                grounding: ch.grounding,
                text: ch.text,
              }),
            ),
          }),
        ),
      };

      termTopic.terms.push(term);
    });
  });

  const formatted: TermCompilerDataType = {
    term_compiler_id: termCompilerBaseData?._id,
    compiler_name: termCompilerBaseData?.compiler_name,
    eg_nid: termCompilerBaseData?.eg_nid,
    date_created: formatDate(termCompilerBaseData?.createdAt, 'DD/MM/YYYY'),
    status: termCompilerBaseData?.status,
    selected_terms: Object.values(topicsMap),
  };

  return formatted;
};

export const updateCompiler = async (
  fields: any,
  compiler_id: string,
  openSnackbar: any,
) => {
  try {
    await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.updateCompiler,
      {
        compiler_id: compiler_id,
        fields,
      },
    );
  } catch (error: any) {
    openSnackbar('Contract Selection Failed', 'error', 6000);
    throw error;
  }
};

export const extractTermCompilerMetaData = async (
  data: any,
  openSnackbar: any,
) => {
  try {
    openSnackbar(
      'Regeneration is in progress.Please revisit after some time.',
      'info',
      6000,
    );
    await axios.post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.extractTermCompilerMetadata,
      data,
    );
  } catch (error: any) {
    throw error;
  }
};

export const fetchUseCases = async (
  setUseCases: React.Dispatch<React.SetStateAction<any[]>>,
  payload: any = {},
) => {
  await axios
    .post(
      properties.aiqBaseUrl +
        properties.contractBaseUrl +
        properties.getUseCases,
      payload,
    )
    .then((res: any) => {
      if (res.status === 200) {
        setUseCases(res?.data?.data || []);
      }
    });
};

export const nullDataColumns: Columns[] = [
  {
    name: 'Topic',
    width: '25%',
    align: 'left',
    cellStyles: cellStyles3,
  },
  {
    name: 'Missing Terms',
    width: '',
    align: 'left',
    cellStyles: cellStyles3,
  },
];

export const formatNullTableData = (
  selected_terms: SelectedTopicDataType[],
  classes: Record<string, string>,
) => {
  const tableData: any[] = [];

  if (selected_terms.length > 0) {
    selected_terms.forEach((item) => {
      const section: any[] = [
        <Typography fontSize="14px" fontType="medium" lineHeight="24px">
          {item.label}
        </Typography>,
      ];

      item.term_topics.forEach((row: SelectedTermsDataType) => {
        // collect only null/undefined terms
        const nullTerms = row.terms
           
          .filter((t) => t.value === null || t.value === undefined)
           
          .map((t) => t.term);

        if (nullTerms.length > 0) {
          const termRow = [
            <Box key={`${row.topic}-topic`} className={classes.parameterBox}>
              <Typography fontSize="14px" lineHeight="24px">
                {row.topic}
              </Typography>
            </Box>,
            <Box key={`${row.topic}-nulls`} className={classes.parameterBox}>
              <Typography fontSize="14px" lineHeight="24px">
                {nullTerms.join(', ')}
              </Typography>
            </Box>,
            '#fff',
          ];

          section.push(termRow);
        }
      });

      // only push if section has rows
      if (section.length > 1) {
        tableData.push(section);
      }
    });
  }

  return tableData;
};
